import { DateTimePicker } from "components/DatePicker/DateTimePicker";
import TrainTravelClassDropdown from "components/dropdowns/reactselect/TrainTravelClassDropdown";
import FileList from "components/FileUpload/filelist";
import { ManageTrainPassengerFilesModal } from "components/FileUpload/ManageMultipleFiles/ManageTrainFilesModal";
import { emptySubmitChangeFunc } from "components/MainSectionTemplate/emptySubmitChangeFunc";
import { RenderEditOptions, Section } from "components/MainSectionTemplate/types";
import ArtistModalPicker from "components/Picker/ArtistPicker/ArtistModalPicker";
import { ContactMultiModalPicker } from "components/Picker/ContactPicker/ContactMultiModalPicker";
import { CompanySidePanel } from "components/SidePanel/Company/CompanySidePanel";
import { SidePanelContactList } from "components/SidePanel/contact/SidePanelContactList";
import SmallDetails from "components/SmallDetails/SmallDetails";
import { GooglePlaceInput, GooglePlaceInputValue } from "components/ValidatedFields/GooglePlaceInput";
import OptionalTextInput from "components/ValidatedFields/OptionalTextInput";
import { LinkedShowSimplePanel } from "document/linkshow/LinkedShowSimplePanel";
import { FormatArrivalTime } from "flights/lookup/FlightSearchResult";
import moment from "moment";
import React from "react";
import { formatTime, formatWrittenDayMonthYear } from "utilities";
import { TrainInfo } from "./useTrainDetail";
import { TrainFileUpload } from "components/FileUpload/FileUploadWithValue";
import OptionalTextAreaInput, { OptionalTextAreaDetails } from "components/ValidatedFields/OptionalTextAreaInput";
import { type LinkShowToTravelConfig } from "components/LinkShowToTravelButton";

export const trainSections = (
  state: TrainInfo,
  linkedShows: server.dto.LinkedShow[],
  onFilesChanged: () => Promise<void>,
  getUpdateTrainDetails: (trainInfo: TrainInfo) => server.dto.UpdateTrainInstruction,
  linkShowToTravel?: LinkShowToTravelConfig,
): Array<Section> => [{
  key: "train",
  type: "regular",
  title: RESX.Train.resxTrain,
  fields: [{
    key: "artist",
    label: site.currentEnvironment.functionGroups.artist.singular,
    isEmpty: !state.artist,
    render: () => <CompanySidePanel type="artist"
      companyId={state.artist.artistId}
      trigger={<a>{state.artist.name}</a>}
      fallbackIfNoPermission={<>{state.artist.name}</>}
    />,
    inPageEdit: {
      hoverEdit: true,
      render: ({ onChange, cancel, value }) => <div className="main-section-template-fields-padding">
        <ArtistModalPicker
          inPageEditing={{ onChange, cancel, hasValue: Boolean(value) }}
          securityScope="limitByUserAccess"
          canCreateNew={false}
        />
      </div>,
      fullWidth: false,
      type: "custom",
      initialValue: state.artist,
      submitChange: async (artist: server.dto.ArtistSmall) => window.CoreApi.Train.UpdateTrain(
        getUpdateTrainDetails({ ...state, artist }),
      ),
    },
  }, {
    key: "travellers",
    label: RESX.Flight.Travellers,
    isEmpty: !state.travellers || state.travellers.length === 0,
    render: () => <SidePanelContactList contacts={state.travellers} />,
    inPageEdit: {
      fullWidth: true,
      render: ({ value, onChange, cancel }) => <div className="main-section-template-fields-padding">
        <ContactMultiModalPicker
          inPageEditing={{ cancel }}
          value={value}
          onChange={contacts => onChange(contacts, true)}
          canCreateNew
          required={false}
          validatorKey="contacts"
        />
      </div>,
      initialValue: state.travellers,
      type: "custom",
      submitChange: (travellers: server.dto.ContactSmallV2[]) => window.CoreApi.Train.UpdateTrain({
        ...getUpdateTrainDetails({ ...state, travellers }),
      }),
    }
  }, {
    key: "bookingReference",
    label: RESX.Flight.BookingCode,
    isEmpty: !state.bookingReference,
    render: () => <div>{state.bookingReference}</div>,
    inPageEdit: {
      render: ({ value, onChange, submit, cancel }: RenderEditOptions<string>) => <OptionalTextInput
        focusOnMount
        maxLength={serverReference.constants.train.maxBookingCodeLength}
        value={value}
        onChange={(v) => { onChange(v); }}
        onEnter={() => submit()}
        onEsc={() => cancel()}
      />,
      fullWidth: true,
      type: "confirmOrCancelButton",
      initialValue: state.bookingReference ?? "",
      submitChange: (bookingReference: string) => window.CoreApi.Train.UpdateTrain({
        ...getUpdateTrainDetails({ ...state, bookingReference }),
      }),
    }
  }, {
    key: "travelAgency",
    label: RESX.Flight.TravelAgency,
    isEmpty: !state.travelAgency,
    render: () => <div>{state.travelAgency}</div>,
    inPageEdit: {
      render: ({ value, onChange, submit, cancel }: RenderEditOptions<string>) => <OptionalTextInput
        focusOnMount
        maxLength={serverReference.constants.train.maxTravelAgencyLength}
        value={value}
        onChange={(v) => { onChange(v); }}
        onEnter={() => submit()}
        onEsc={() => cancel()}
      />,
      fullWidth: true,
      type: "confirmOrCancelButton",
      initialValue: state.travelAgency ?? "",
      submitChange: (travelAgency: string) => window.CoreApi.Train.UpdateTrain({
        ...getUpdateTrainDetails({ ...state, travelAgency }),
      }),
    }
  }, {
    key: "notes",
    label: RESX.Note.Notes,
    isEmpty: !state.notes,
    render: () => <OptionalTextAreaDetails>{state.notes}</OptionalTextAreaDetails>,
    inPageEdit: {
      render: ({ value, onChange, submit, cancel }: RenderEditOptions<string>) => <OptionalTextAreaInput
        focusOnMount
        maxLength={serverReference.constants.train.maxNotesLength}
        rows={3}
        value={value}
        onChange={(v) => { onChange(v); }}
        onCtrlEnter={() => submit()}
        onEsc={() => cancel()}
      />,
      fullWidth: true,
      type: "confirmOrCancelButton",
      initialValue: state.notes ?? "",
      submitChange: (notes: string) => window.CoreApi.Train.UpdateTrain({
        ...getUpdateTrainDetails({ ...state, notes }),
      }),
    },
  }, {
    key: "trainNumber",
    label: RESX.Train.TrainNumber,
    isEmpty: !state.trainNumber,
    render: () => <div>{state.trainNumber}</div>,
    inPageEdit: {
      render: ({ value, onChange, submit, cancel }: RenderEditOptions<string>) => <OptionalTextInput
        focusOnMount
        width={100}
        maxLength={serverReference.constants.train.maxTrainNumberLength}
        value={value}
        onChange={(v) => { onChange(v); }}
        onEnter={() => submit()}
        onEsc={() => cancel()}
      />,
      fullWidth: false,
      type: "confirmOrCancelButton",
      initialValue: state.trainNumber ?? "",
      submitChange: (trainNumber: string) => window.CoreApi.Train.UpdateTrain({
        ...getUpdateTrainDetails({ ...state, trainNumber }),
      }),
    },
  }, {
    key: "carriage",
    label: RESX.Train.Carriage,
    isEmpty: !state.carriage,
    render: () => <div>{state.carriage}</div>,
    inPageEdit: {
      render: ({ value, onChange, submit, cancel }: RenderEditOptions<string>) => <OptionalTextInput
        focusOnMount
        width={100}
        maxLength={serverReference.constants.train.maxCarriageLength}
        value={value}
        onChange={(v) => { onChange(v); }}
        onEnter={() => submit()}
        onEsc={() => cancel()}
      />,
      fullWidth: false,
      type: "confirmOrCancelButton",
      initialValue: state.carriage ?? "",
      submitChange: (carriage: string) => window.CoreApi.Train.UpdateTrain({
        ...getUpdateTrainDetails({ ...state, carriage }),
      }),
    }
  }, {
    key: "seats",
    label: RESX.Flight.Seats,
    isEmpty: !state.seats,
    render: () => <div>{state.seats}</div>,
    inPageEdit: {
      render: ({ value, onChange, submit, cancel }: RenderEditOptions<string>) => <OptionalTextInput
        focusOnMount
        width={200}
        maxLength={serverReference.constants.train.maxSeatsLength}
        value={value}
        onChange={(v) => { onChange(v); }}
        onEnter={() => submit()}
        onEsc={() => cancel()}
      />,
      fullWidth: true,
      type: "confirmOrCancelButton",
      initialValue: state.seats ?? "",
      submitChange: (seats: string) => window.CoreApi.Train.UpdateTrain({
        ...getUpdateTrainDetails({ ...state, seats }),
      }),
    },
  }, {
    key: "travelClass",
    label: RESX.Flight.TravelClass,
    isEmpty: !state.travelClass || state.travelClass.value === "unknown",
    render: () => <SmallDetails
      label={state.travelClass.text}
    />,
    inPageEdit: {
      render: ({ value, onChange, cancel }: RenderEditOptions<server.dto.TrainTravelClass>) => <TrainTravelClassDropdown
        value={value}
        onChange={(travelClass) => {
          onChange(travelClass, true);
        }}
        onEsc={() => cancel()}
        config={{
          focusOnMount: true,
          isSearchable: true,
          openMenuOnFocus: true,
        }}
      />,
      fullWidth: true,
      type: "custom",
      initialValue: state.travelClass,
      submitChange: (travelClass: server.dto.TrainTravelClass) => window.CoreApi.Train.UpdateTrain(
        getUpdateTrainDetails({ ...state, travelClass }),
      ),
    }
  }]
}, {
  key: "departureArrival",
  title: "",
  hideTitle: true,
  type: "regular",
  fields: [{
    key: "departureArrivalLabel",
    label: "",
    left: {
      key: "departureLabel",
      render: () => <h3>{RESX.Flight.Departure}</h3>
    },
    right: {
      key: "arrivalLabel",
      render: () => <h3>{RESX.Flight.Arrival}</h3>
    }
  }, {
    key: "date",
    label: RESX.DateTime.lblDate,
    left: {
      key: "departureLocal",
      render: () => <div className="flex-column">
        <div>{formatTime(state.departureLocal)}</div>
        <div>{formatWrittenDayMonthYear(state.departureLocal)}</div>
      </div>,
      inPageEdit: {
        render: ({ value, onChange, submit, cancel }: RenderEditOptions<moment.Moment>) => <DateTimePicker
          value={value}
          onChange={v => onChange(v)}
          validatorKey="departureDate"
          minDate={moment(serverReference.constants.appointment.minDate)}
          maxDate={moment(serverReference.constants.appointment.maxDate)}
          onEnter={submit}
          onEsc={cancel}
        />,
        type: "confirmOrCancelButton",
        fullWidth: true,
        initialValue: state.departureLocal,
        submitChange: (departureLocal: moment.Moment) => {
          const diff = departureLocal.diff(state.departureLocal, "minutes");
          const arrivalLocal = state.arrivalLocal.clone().add(diff, "minutes");
          return window.CoreApi.Train.UpdateTrain({
            ...getUpdateTrainDetails({ ...state, departureLocal, arrivalLocal }),
          })
        }
      }
    },
    right: {
      key: "arrivalLocal",
      render: () => <FormatArrivalTime departure={state.departureLocal} arrival={state.arrivalLocal} />,
      inPageEdit: {
        render: ({ value, onChange, submit, cancel }: RenderEditOptions<moment.Moment>) => <DateTimePicker
          value={value}
          onChange={v => onChange(v)}
          validatorKey="arrivalDate"
          minDate={state.departureLocal.clone().add(serverReference.constants.flight.maxArrivalBeforeDepartureHours, "hours")}
          maxDate={state.departureLocal.clone().add(serverReference.constants.flight.maxArrivalAfterDepartureHours, "hours")}
          onEnter={submit}
          onEsc={cancel}
        />,
        type: "confirmOrCancelButton",
        fullWidth: true,
        initialValue: state.arrivalLocal,
        submitChange: (arrivalLocal: moment.Moment) => window.CoreApi.Train.UpdateTrain({
          ...getUpdateTrainDetails({ ...state, arrivalLocal }),
        }),
      }
    }
  }, {
    key: "station",
    label: RESX.Train.Station,
    left: {
      key: "departureStation",
      isEmpty: !state.departureStation || !state.departureStation.googlePlaceId,
      render: () => <SmallDetails
        label={state.departureStation.name}
        subLabel={state.departureStation.address}
      />,
      inPageEdit: {
        render: ({ onChange }: RenderEditOptions<GooglePlaceInputValue>) => <GooglePlaceInput
          calledFrom="train"
          placeType={"trainStation"}
          focusOnMount
          onPlaceSelected={(place) => onChange(place, true)}
        />,
        fullWidth: true,
        type: "custom",
        initialValue: state.departureStation,
        submitChange: (departureStation: GooglePlaceInputValue) => window.CoreApi.Train.UpdateTrain({
          ...getUpdateTrainDetails({
            ...state,
            departureStation
          }),
        }),
      }
    },
    right: {
      key: "arrivalStation",
      isEmpty: !state.arrivalStation || !state.arrivalStation.googlePlaceId,
      render: () => <SmallDetails
        label={state.arrivalStation.name}
        subLabel={state.arrivalStation.address}
      />,
      inPageEdit: {
        render: ({ onChange }: RenderEditOptions<GooglePlaceInputValue>) => <GooglePlaceInput
          calledFrom="train"
          placeType={"trainStation"}
          focusOnMount
          onPlaceSelected={(place) => onChange(place, true)}
        />,
        fullWidth: true,
        type: "custom",
        initialValue: state.arrivalStation,
        submitChange: (arrivalStation: GooglePlaceInputValue) => window.CoreApi.Train.UpdateTrain({
          ...getUpdateTrainDetails({
            ...state,
            arrivalStation,
          }),
        }),
      }
    }
  }, {
    key: "platform",
    label: RESX.Train.Platform,
    left: {
      key: "departurePlatform",
      isEmpty: !state.departurePlatform,
      render: () => <div>{state.departurePlatform}</div>,
      inPageEdit: {
        render: ({ value, onChange, submit, cancel }: RenderEditOptions<string>) => <OptionalTextInput
          focusOnMount
          width={60}
          maxLength={serverReference.constants.flight.maxTerminalLength}
          value={value}
          onChange={(v) => { onChange(v); }}
          onEnter={() => submit()}
          onEsc={() => cancel()}
        />,
        fullWidth: false,
        type: "confirmOrCancelButton",
        initialValue: state.departurePlatform ?? "",
        submitChange: (departurePlatform: string) => window.CoreApi.Train.UpdateTrain({
          ...getUpdateTrainDetails({ ...state, departurePlatform }),
        }),
      }
    },
    right: {
      key: "arrivalPlatform",
      isEmpty: !state.arrivalPlatform,
      render: () => <div>{state.arrivalPlatform}</div>,
      inPageEdit: {
        render: ({ value, onChange, submit, cancel }: RenderEditOptions<string>) => <OptionalTextInput
          focusOnMount
          width={60}
          maxLength={serverReference.constants.train.maxPlatformLength}
          value={value}
          onChange={(v) => { onChange(v); }}
          onEnter={() => submit()}
          onEsc={() => cancel()}
        />,
        fullWidth: false,
        type: "confirmOrCancelButton",
        initialValue: state.arrivalPlatform ?? "",
        submitChange: (arrivalPlatform: string) => window.CoreApi.Train.UpdateTrain({
          ...getUpdateTrainDetails({ ...state, arrivalPlatform }),
        }),
      }
    }
  }, {
    key: "filesEmpty",
    label: RESX.File.Files,
    hidden: state.files.length > 0,
    render: () => <TrainFileUpload
      trainId={state.trainId}
      onUploadComplete={onFilesChanged}
    />
  }, {
    key: "files",
    label: RESX.File.Files,
    hidden: state.files.length === 0,
    render: () => <FileList editDisabled files={state.files} onAddFilesClick={() => { }} />,
    inPageEdit: {
      render: ({ cancel }) => <ManageTrainPassengerFilesModal
        trainId={state.trainId}
        isOpen
        onClose={cancel}
        canEdit={site.permissions.train.canCreate}
        files={state.files}
        onFilesChanged={async () => {
          await onFilesChanged();
        }}
      />,
      type: "custom",
      fullWidth: true,
      initialValue: state.files,
      submitChange: () => emptySubmitChangeFunc(),

    }
  }]
}, {
  key: "linkedShows",
  type: "custom",
  render: () => <LinkedShowSimplePanel
    key="linkedShows"
    linkedShows={linkedShows}
    linkShowToTravel={linkShowToTravel}
  />
}];
